import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = { name: '', email: '', password: '', role: 'customer_viewer', customer_id: '' };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users', { params: { limit: 50 } });
      setUsers(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/customers', { params: { limit: 200 } }).then(r => setCustomers(r.data.data)).catch(() => {});
  }, []);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setModal('create'); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: '', role: u.role, customer_id: u.customer_id || '' }); setEditId(u.id); setModal('edit'); };
  const close = () => setModal(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (modal === 'create') await api.post('/users', payload);
      else await api.put(`/users/${editId}`, payload);
      close(); load();
    } catch (err) {
      alert(err.response?.data?.message || 'Hata oluştu');
    } finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (u.id === me.id) return alert('Kendinizi devre dışı bırakamazsınız');
    if (!window.confirm('Kullanıcıyı devre dışı bırak?')) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  const roleLabel = (r) => ({ super_admin: 'Süper Admin', customer_admin: 'Müşteri Admin', customer_viewer: 'Görüntüleyici' }[r] || r);
  const roleBadge = (r) => ({ super_admin: 'badge-red', customer_admin: 'badge-blue', customer_viewer: 'badge-yellow' }[r] || 'badge-yellow');

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div><h1>Kullanıcılar</h1><p>{total} kullanıcı kayıtlı</p></div>
        <button className="btn btn-primary" onClick={openCreate}>+ Kullanıcı Ekle</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Ad Soyad</th><th>Email</th><th>Rol</th><th>Müşteri</th><th>Son Giriş</th><th>Durum</th><th>İşlem</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>Yükleniyor...</td></tr>
              ) : users.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.name}</strong>{u.id === me.id && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--accent2)' }}>(siz)</span>}</td>
                  <td style={{ color: 'var(--text2)' }}>{u.email}</td>
                  <td><span className={`badge ${roleBadge(u.role)}`}>{roleLabel(u.role)}</span></td>
                  <td style={{ color: 'var(--text2)' }}>{u.customer_name || '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.last_login ? new Date(u.last_login).toLocaleString('tr-TR') : 'Hiç'}</td>
                  <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`}>{u.is_active ? 'Aktif' : 'Pasif'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Düzenle</button>
                      {u.id !== me.id && <button className="btn btn-danger btn-sm" onClick={() => deactivate(u)}>Devre Dışı</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
          <div className="modal admin-form-modal">
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Yeni Kullanıcı' : 'Kullanıcı Düzenle'}</h3>
              <button className="btn btn-secondary btn-icon" onClick={close}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label>Ad Soyad *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
                <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Şifre {modal === 'edit' && '(boş bırakılırsa değişmez)'}</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={modal === 'create'} />
                </div>
                <div className="form-group">
                  <label>Rol</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="customer_viewer">Görüntüleyici</option>
                    <option value="customer_admin">Müşteri Admin</option>
                    <option value="super_admin">Süper Admin</option>
                  </select>
                </div>
              </div>
              {form.role !== 'super_admin' && (
                <div className="form-group">
                  <label>Müşteri</label>
                  <select value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}>
                    <option value="">Seçin...</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={close}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
